// Traffic-light indicator: three small dots (green / yellow / red).
// Exactly one (or none) is lit. Drives the visibility marker next to
// clients, properties, and appointments.
//
// State mapping:
//   off    → appointment scheduled, date not yet reached. All dots dark.
//   yellow → reservation window (≤30 days post-appointment). Middle dot lit.
//   green  → contract signed. Top dot lit.
//   red    → no contract / cancelled / reservation expired. Bottom dot lit.
//   none   → no related appointment. Hidden (returns null).

import { useTranslation } from 'react-i18next';

export type TrafficLightState = 'off' | 'yellow' | 'green' | 'red' | 'none';

interface Props {
  state: TrafficLightState | null | undefined;
  /** Inline horizontal variant (default) is compact for table rows.
   *  Vertical is for detail views where space allows the full pole. */
  orientation?: 'horizontal' | 'vertical';
  /** Tooltip override — defaults to localized state label. */
  title?: string;
  className?: string;
}

// Active lights: full saturation + colored halo.
const LIT_COLORS = {
  green: { bg: '#2f7a3e', glow: 'rgba(47,122,62,0.65)' },
  yellow: { bg: '#d49b1f', glow: 'rgba(212,155,31,0.65)' },
  red: { bg: '#c23838', glow: 'rgba(194,56,56,0.65)' },
} as const;

// "Off" lights: still clearly red / yellow / green — just unilluminated.
// Mirrors a real traffic light where the lenses keep their colour even
// when no bulb is powered. The tester's "where are my lights?" feedback
// came from a previous version where off slots were near-white.
const DIM_COLORS = {
  green: 'rgba(47,122,62,0.45)',
  yellow: 'rgba(212,155,31,0.45)',
  red: 'rgba(194,56,56,0.45)',
} as const;

export function TrafficLight({
  state,
  orientation = 'horizontal',
  title,
  className,
}: Props) {
  const { t } = useTranslation();
  if (!state || state === 'none') return null;

  const labels: Record<Exclude<TrafficLightState, 'none'>, string> = {
    off: t('traffic_light.off', 'Termin geplant — Datum noch nicht erreicht'),
    yellow: t('traffic_light.yellow', 'Reservierung — 30 Tage Frist läuft'),
    green: t('traffic_light.green', 'Auftrag bekommen'),
    red: t('traffic_light.red', 'Kein Auftrag / Frist abgelaufen'),
  };

  const dotStyle = (color: 'green' | 'yellow' | 'red'): React.CSSProperties => {
    const isLit = state === color;
    if (isLit) {
      return {
        backgroundColor: LIT_COLORS[color].bg,
        boxShadow: `0 0 0 1.5px ${LIT_COLORS[color].bg}, 0 0 10px 1.5px ${LIT_COLORS[color].glow}`,
      };
    }
    return {
      backgroundColor: DIM_COLORS[color],
      border: '1px solid rgba(0,0,0,0.12)',
    };
  };

  return (
    <span
      role="img"
      aria-label={labels[state as Exclude<TrafficLightState, 'none'>]}
      title={title ?? labels[state as Exclude<TrafficLightState, 'none'>]}
      className={`inline-flex shrink-0 ${
        orientation === 'vertical' ? 'flex-col gap-1' : 'flex-row gap-1'
      } items-center rounded-full border border-line bg-card px-1.5 py-1 shadow-soft ${
        className ?? ''
      }`}
    >
      <span className="block h-2.5 w-2.5 rounded-full" style={dotStyle('green')} />
      <span className="block h-2.5 w-2.5 rounded-full" style={dotStyle('yellow')} />
      <span className="block h-2.5 w-2.5 rounded-full" style={dotStyle('red')} />
    </span>
  );
}
